import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Wordmark } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/api/http";
import type { SessionDto, UserDto } from "@/api/types";
import { go, nextPath } from "@/lib/nav";
import { useStore } from "@/model/store";

// Sign up, sign in, verify email, forgot password → reset (plan §6.2). Codes come by email through
// Twilio Email; the API's dev stub uses 000000.

export function AuthShell({ title, sub, children, footer }: { title: string; sub?: React.ReactNode; children: React.ReactNode; footer?: React.ReactNode }) {
	return (
		<div className="flex min-h-screen items-start justify-center bg-background px-4 py-16 sm:items-center sm:py-8">
			<div className="w-full max-w-sm">
				<div className="mb-8 flex justify-center text-foreground">
					<Wordmark />
				</div>
				<div className="rounded-xl border bg-card p-6 shadow-xs">
					<h1 className="text-xl font-semibold tracking-tight">{title}</h1>
					{sub && <p className="mt-1 text-sm text-muted-foreground">{sub}</p>}
					<div className="mt-5">{children}</div>
				</div>
				{footer && <div className="mt-4 text-center text-sm text-muted-foreground">{footer}</div>}
			</div>
		</div>
	);
}

function Field({ id, label, ...props }: { id: string; label: string } & React.ComponentProps<typeof Input>) {
	return (
		<div className="space-y-1.5">
			<Label htmlFor={id}>{label}</Label>
			<Input id={id} className="h-9" {...props} />
		</div>
	);
}

function useSubmit() {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	async function run(fn: () => Promise<void>) {
		setBusy(true);
		setError(null);
		try {
			await fn();
		} catch (e) {
			setError(e instanceof ApiError ? e.messages.join(" ") : "Something went wrong. Try again.");
		} finally {
			setBusy(false);
		}
	}
	return { busy, error, run };
}

function ErrorLine({ error }: { error: string | null }) {
	if (!error) return null;
	return (
		<p role="alert" className="text-sm text-[var(--risk-ink)]">
			{error}
		</p>
	);
}

/** A carried-over `?next=` for links between the auth screens. */
function withNext(path: string, params: URLSearchParams) {
	const next = params.get("next");
	return next ? `${path}?next=${encodeURIComponent(next)}` : path;
}

export function SignInPage() {
	const [params] = useSearchParams();
	const setSession = useStore((s) => s.setSession);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const { busy, error, run } = useSubmit();
	return (
		<AuthShell
			title="Sign in"
			sub="to your worldview."
			footer={
				<>
					New here?{" "}
					<Link className="font-medium text-foreground underline underline-offset-2" to={withNext("/signup", params)}>
						Create an account
					</Link>
				</>
			}
		>
			<form
				className="space-y-4"
				onSubmit={(e) => {
					e.preventDefault();
					void run(async () => {
						const s = await api.post<SessionDto>("/auth/signin", { email, password });
						setSession(s);
						go(nextPath(params, s));
					});
				}}
			>
				<Field id="si-email" label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required />
				<Field
					id="si-password"
					label="Password"
					type="password"
					autoComplete="current-password"
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					required
				/>
				<ErrorLine error={error} />
				<Button type="submit" className="h-9 w-full" disabled={busy}>
					{busy ? "Signing in…" : "Sign in"}
				</Button>
				<p className="text-center text-sm">
					<Link className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline" to={withNext("/forgot", params)}>
						Forgot your password?
					</Link>
				</p>
			</form>
		</AuthShell>
	);
}

export function SignUpPage() {
	const [params] = useSearchParams();
	const navigate = useNavigate();
	const setSession = useStore((s) => s.setSession);
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const { busy, error, run } = useSubmit();
	return (
		<AuthShell
			title="Create an account"
			sub="Your own private worldview: events and metrics you can drag, link and run scenarios on."
			footer={
				<>
					Have an account?{" "}
					<Link className="font-medium text-foreground underline underline-offset-2" to={withNext("/signin", params)}>
						Sign in
					</Link>
				</>
			}
		>
			<form
				className="space-y-4"
				onSubmit={(e) => {
					e.preventDefault();
					void run(async () => {
						const s = await api.post<SessionDto>("/auth/signup", { name, email, password });
						setSession(s);
						navigate(`/verify?next=${encodeURIComponent(nextPath(params, s))}`);
					});
				}}
			>
				<Field id="su-name" label="Name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
				<Field id="su-email" label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
				<Field
					id="su-password"
					label="Password"
					type="password"
					autoComplete="new-password"
					minLength={8}
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					required
				/>
				<p className="-mt-2 text-xs text-muted-foreground">At least 8 characters.</p>
				<ErrorLine error={error} />
				<Button type="submit" className="h-9 w-full" disabled={busy}>
					{busy ? "Creating…" : "Create account"}
				</Button>
			</form>
		</AuthShell>
	);
}

function DevCodeHint() {
	if (!import.meta.env.DEV) return null;
	return <p className="text-xs text-muted-foreground">Development: with email not configured, the code is 000000.</p>;
}

export function VerifyPage() {
	const [params] = useSearchParams();
	const user = useStore((s) => s.user);
	const phase = useStore((s) => s.phase);
	const [code, setCode] = useState("");
	const { busy, error, run } = useSubmit();
	const next = params.get("next") ?? "/";
	const safeNext = next.startsWith("/") && !next.startsWith("//") ? next : "/";

	useEffect(() => {
		if (phase === "signed-out") go(`/signin?next=${encodeURIComponent(`/verify?next=${encodeURIComponent(safeNext)}`)}`);
	}, [phase, safeNext]);

	if (user?.email_verified)
		return (
			<AuthShell title="Email verified" sub={user.email}>
				<Button className="h-9 w-full" onClick={() => go(safeNext)}>
					Continue
				</Button>
			</AuthShell>
		);

	return (
		<AuthShell
			title="Check your email"
			sub={
				<>
					We sent a code to <span className="font-medium text-foreground">{user?.email ?? "your email"}</span>. You need it to connect an AI app to your
					worldview.
				</>
			}
			footer={
				<button type="button" className="underline-offset-2 hover:text-foreground hover:underline" onClick={() => go(safeNext)}>
					Skip for now
				</button>
			}
		>
			<form
				className="space-y-4"
				onSubmit={(e) => {
					e.preventDefault();
					void run(async () => {
						const r = await api.post<{ user: UserDto }>("/auth/email/verification/confirm", { code });
						useStore.setState({ user: r.user });
						toast.success("Email verified");
						go(safeNext);
					});
				}}
			>
				<Field
					id="v-code"
					label="Code"
					inputMode="numeric"
					autoComplete="one-time-code"
					value={code}
					onChange={(e) => setCode(e.target.value.trim())}
					autoFocus
					required
				/>
				<DevCodeHint />
				<ErrorLine error={error} />
				<Button type="submit" className="h-9 w-full" disabled={busy}>
					{busy ? "Checking…" : "Verify email"}
				</Button>
				<button
					type="button"
					className="w-full text-center text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
					onClick={() =>
						void run(async () => {
							await api.post("/auth/email/verification");
							toast("A new code is on its way.");
						})
					}
				>
					Send a new code
				</button>
			</form>
		</AuthShell>
	);
}

export function ForgotPage() {
	const [params] = useSearchParams();
	const navigate = useNavigate();
	const [email, setEmail] = useState("");
	const { busy, error, run } = useSubmit();
	return (
		<AuthShell
			title="Reset your password"
			sub="We’ll email you a code."
			footer={
				<Link className="underline-offset-2 hover:text-foreground hover:underline" to={withNext("/signin", params)}>
					Back to sign in
				</Link>
			}
		>
			<form
				className="space-y-4"
				onSubmit={(e) => {
					e.preventDefault();
					void run(async () => {
						await api.post("/auth/password/reset", { email });
						const next = params.get("next");
						navigate(`/reset?email=${encodeURIComponent(email)}${next ? `&next=${encodeURIComponent(next)}` : ""}`);
					});
				}}
			>
				<Field id="f-email" label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required />
				<ErrorLine error={error} />
				<Button type="submit" className="h-9 w-full" disabled={busy}>
					{busy ? "Sending…" : "Send code"}
				</Button>
			</form>
		</AuthShell>
	);
}

export function ResetPage() {
	const [params] = useSearchParams();
	const setSession = useStore((s) => s.setSession);
	const [email, setEmail] = useState(params.get("email") ?? "");
	const [code, setCode] = useState("");
	const [password, setPassword] = useState("");
	const { busy, error, run } = useSubmit();
	return (
		<AuthShell title="Choose a new password" sub="If that email has an account, a code is on its way. Every other session is signed out.">
			<form
				className="space-y-4"
				onSubmit={(e) => {
					e.preventDefault();
					void run(async () => {
						const s = await api.post<SessionDto>("/auth/password/reset/confirm", { email, code, password });
						setSession(s);
						toast.success("Password changed");
						go(nextPath(params, s));
					});
				}}
			>
				<Field id="r-email" label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
				<Field
					id="r-code"
					label="Code"
					inputMode="numeric"
					autoComplete="one-time-code"
					value={code}
					onChange={(e) => setCode(e.target.value.trim())}
					autoFocus
					required
				/>
				<DevCodeHint />
				<Field
					id="r-password"
					label="New password"
					type="password"
					autoComplete="new-password"
					minLength={8}
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					required
				/>
				<ErrorLine error={error} />
				<Button type="submit" className="h-9 w-full" disabled={busy}>
					{busy ? "Saving…" : "Set password and sign in"}
				</Button>
			</form>
		</AuthShell>
	);
}

/** `/`: the first workspace when signed in, else sign in. */
export function Landing() {
	const phase = useStore((s) => s.phase);
	const workspaces = useStore((s) => s.workspaces);
	const navigate = useNavigate();
	useEffect(() => {
		if (phase === "signed-out") navigate("/signin", { replace: true });
		if (phase === "signed-in") go(nextPath(new URLSearchParams(), { workspaces }));
	}, [phase, workspaces, navigate]);
	return <Booting />;
}

export function Booting({ message = "Loading…" }: { message?: React.ReactNode }) {
	return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">{message}</div>;
}
